import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent, merge} from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ConnectivityStatusComponent } from '../../shared/components/connectivity-status/connectivity-status.component';
import { HealthService } from '../http-services/health.service';
import { NotificationHubStateService } from '../states/notification-hub.state.service';

type ConnectivityState = 'online' | 'offline' | 'server-down';

@Injectable({
  providedIn: 'root'
})
export class ConnectivityService {

  private dialogRef?: MatDialogRef<ConnectivityStatusComponent>;
  private currentState: ConnectivityState = 'online';

  private readonly normalInterval = 30000;
  private readonly recoveryInterval = 5000;

  constructor(
    private readonly dialog: MatDialog,
    private readonly healthService: HealthService,
    private readonly notificationHubStateService: NotificationHubStateService
  ) {

    merge(
      fromEvent(globalThis, 'online'),
      fromEvent(globalThis, 'offline')
    ).subscribe(() => this.checkStatus());

    this.notificationHubStateService.isConnected$.pipe(debounceTime(3000))
      .subscribe(() => {
        this.checkStatus();
      });
  }

  private checkStatus(): void {
    if (!navigator.onLine) {
      this.updateState('offline');
      return;
    }
    
    this.healthService.ready()
      .subscribe({
        next: () => {
          this.updateState('online');
        },
        error: (err : HttpErrorResponse) => {
          if (err.status === 429) {
              return; // Only handle server down errors, ignore rate limiting
          }
          this.updateState('server-down');
        }
      });
  }

  private updateState(state: ConnectivityState): void {
    if (this.currentState === state) {
      return;
    }

    this.currentState = state;
    if (state === 'online') {
      this.hideDialog();
      return;
    }

    this.showDialog(state);
  }

  private showDialog(type: 'offline' | 'server-down'): void {
    if (this.dialogRef) {
      this.dialogRef.componentInstance.data = { type };
      return;
    }

    this.dialogRef = this.dialog.open(
      ConnectivityStatusComponent,
      {
        data: { type },
        hasBackdrop: false,
        disableClose: true,
        position: {
          right: '20px',
          bottom: '80px'
        },
        panelClass: 'connectivity-dialog'
      }
    );
  }

  private hideDialog(): void {
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  private startMonitoring(): void {
    this.checkStatus();
    setTimeout(() => {
      this.startMonitoring();
    }, this.currentState === 'online'
      ? this.normalInterval
      : this.recoveryInterval);
  }
}